import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  Bot,
  Phone,
  BarChart3,
  Settings,
  Shield,
  FileText,
  LogOut,
  ChevronRight,
  Building,
  Package
} from 'lucide-react';

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
  { icon: Users, label: 'Users', path: '/admin/users' },
  { icon: Building, label: 'Clients', path: '/admin/sub-admins' },
  { icon: Package, label: 'Resource Quotas', path: '/admin/resources', highlight: true },
  { icon: Bot, label: 'Voice Agents', path: '/admin/agents' },
  { icon: Phone, label: 'Phone Numbers', path: '/admin/phone-numbers' },
  { icon: BarChart3, label: 'Analytics', path: '/admin/analytics' },
  { icon: Shield, label: 'Permissions', path: '/admin/permissions' },
  { icon: FileText, label: 'Audit Logs', path: '/admin/logs' },
  { icon: Settings, label: 'Settings', path: '/admin/settings' },
];

const AdminSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  return (
    <div className="w-64 bg-[#0d1117] border-r border-gray-800 min-h-screen flex flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-gray-800">
        <a href="/" className="text-xl font-bold text-white tracking-tight">
          AI<span className="text-teal-400">CONNEQTORS</span>
        </a>
        <p className="text-gray-500 text-xs mt-1">Admin Panel</p>
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-teal-400 flex items-center justify-center text-black font-bold">
            {user?.username?.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-white font-medium text-sm">{user?.username}</p>
            <p className="text-teal-400 text-xs">{user?.role}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <ul className="space-y-1">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path}>
                <button
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group ${
                    isActive
                      ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30'
                      : item.highlight 
                        ? 'text-purple-400 hover:text-purple-300 hover:bg-purple-400/10 border border-purple-400/20'
                        : 'text-gray-400 hover:text-white hover:bg-[#1a1a1f]'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="flex-1 text-left text-sm font-medium">{item.label}</span>
                  {item.highlight && !isActive && <span className="text-[10px] px-1.5 py-0.5 bg-purple-400/20 text-purple-400 rounded">NEW</span>}
                  {isActive && <ChevronRight className="w-4 h-4" />}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-gray-800">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-400/10 transition-all duration-200"
        >
          <LogOut className="w-5 h-5" />
          <span className="text-sm font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
};

export default AdminSidebar;
